import { useEffect, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { Billboard, Text } from "@react-three/drei";
import { UserAvatar } from "@/components/clinic/UserAvatar";

const SPEED = 2.4;
const B = { minX: -6.3, maxX: 6.3, minZ: -3.3, maxZ: 6.4 };
export const TABLES = [[-4, -1], [-1.5, 2], [2.5, 1.5], [4.5, -2], [-4.5, 3.2]];
const ROUND = [...TABLES.map(([x, z]) => [x, z, 1.45]), [6, -6, 0.9], [-6, 6, 0.9]];
export const SEATS = Object.fromEntries(TABLES.flatMap(([x, z]) => [0.95, -0.95].map((dx) => {
  const id = `${x}_${z}_${dx > 0 ? "d" : "s"}`;
  return [id, { id, x: x + dx, z, yaw: dx > 0 ? Math.PI / 2 : -Math.PI / 2, ax: x + Math.sign(dx) * 1.65, az: z }];
})));

export const cafeMe = { x: 0, z: 4.5, yaw: 0, target: null, path: [], idle: 0, sit: null, pendingSeat: null };
export const taken = new Set();
const keys = new Set();

function free(x, z) {
  if (x < B.minX || x > B.maxX || z < B.minZ || z > B.maxZ) return false;
  return !ROUND.some(([cx, cz, r]) => Math.hypot(x - cx, z - cz) < r);
}

function randomSpot() {
  for (let i = 0; i < 30; i++) {
    const p = { x: B.minX + Math.random() * (B.maxX - B.minX), z: B.minZ + Math.random() * (B.maxZ - B.minZ) };
    if (free(p.x, p.z)) return p;
  }
  return null;
}

function clearSeg(a, b) {
  const n = Math.ceil(Math.hypot(b.x - a.x, b.z - a.z) / 0.15);
  for (let i = 1; i <= n; i++) if (!free(a.x + ((b.x - a.x) * i) / n, a.z + ((b.z - a.z) * i) / n)) return false;
  return true;
}

const NODES = ROUND.flatMap(([cx, cz, r]) => Array.from({ length: 8 }, (_, i) => ({ x: cx + Math.cos((i * Math.PI) / 4) * (r + 0.4), z: cz + Math.sin((i * Math.PI) / 4) * (r + 0.4) })))
  .filter((p) => free(p.x, p.z));
const dist2 = (a, b) => Math.hypot(a.x - b.x, a.z - b.z);

function plan(from, to) {
  if (clearSeg(from, to)) return [to];
  const best = new Map([["s", { d: 0, path: [] }]]);
  const open = [{ p: from, d: 0, path: [] }];
  let done = null;
  while (open.length) {
    open.sort((a, b) => a.d - b.d);
    const cur = open.shift();
    if (done && cur.d >= done.d) break;
    if (clearSeg(cur.p, to)) { const d = cur.d + dist2(cur.p, to); if (!done || d < done.d) done = { d, path: [...cur.path, to] }; }
    NODES.forEach((n, i) => {
      const d = cur.d + dist2(cur.p, n);
      if ((best.get(i)?.d ?? Infinity) <= d || !clearSeg(cur.p, n)) return;
      best.set(i, { d });
      open.push({ p: n, d, path: [...cur.path, n] });
    });
  }
  return done ? done.path : null;
}

function setGoal(p) {
  const path = plan({ x: cafeMe.x, z: cafeMe.z }, p);
  cafeMe.path = path || [];
  cafeMe.target = path ? path[0] : null;
  return !!path;
}

function standUp() {
  const s = SEATS[cafeMe.sit];
  if (s) { cafeMe.x = s.ax; cafeMe.z = s.az; }
  cafeMe.sit = null;
}

export function walkTo(p) {
  if (!free(p.x, p.z)) return;
  standUp();
  cafeMe.pendingSeat = null;
  setGoal(p);
  cafeMe.idle = 0;
}

export function sitOn(id) {
  const s = SEATS[id];
  if (!s || taken.has(id) || cafeMe.sit === id) return false;
  standUp();
  cafeMe.pendingSeat = id;
  if (!setGoal({ x: s.ax, z: s.az })) cafeMe.pendingSeat = null;
  return true;
}

function arrive() {
  cafeMe.path.shift();
  if (cafeMe.path.length) { cafeMe.target = cafeMe.path[0]; return; }
  cafeMe.target = null;
  const s = SEATS[cafeMe.pendingSeat];
  cafeMe.pendingSeat = null;
  if (!s || taken.has(s.id)) return;
  cafeMe.sit = s.id;
  cafeMe.x = s.x;
  cafeMe.z = s.z;
  cafeMe.yaw = s.yaw;
}

function keyDir() {
  let dx = 0;
  let dz = 0;
  if (keys.has("w") || keys.has("arrowup")) dz -= 1;
  if (keys.has("s") || keys.has("arrowdown")) dz += 1;
  if (keys.has("a") || keys.has("arrowleft")) dx -= 1;
  if (keys.has("d") || keys.has("arrowright")) dx += 1;
  return [dx, dz];
}

function step(d) {
  let [dx, dz] = keyDir();
  const manual = !!(dx || dz);
  if (manual) { standUp(); cafeMe.target = null; cafeMe.pendingSeat = null; }
  if (cafeMe.sit) return;
  if (!manual && cafeMe.target) { dx = cafeMe.target.x - cafeMe.x; dz = cafeMe.target.z - cafeMe.z; }
  const dist = Math.hypot(dx, dz);
  if (cafeMe.target && dist < 0.12) { arrive(); return; }
  if (!dist) {
    cafeMe.idle += d;
    if (cafeMe.idle > 7) { cafeMe.idle = 0; const r = randomSpot(); if (r) setGoal(r); }
    return;
  }
  cafeMe.idle = 0;
  const len = Math.min(manual ? 1 : dist, SPEED * d);
  const base = Math.atan2(dz, dx);
  const turn = [0, 0.5, -0.5, 1, -1, 1.5, -1.5].find((a) => free(cafeMe.x + Math.cos(base + a) * len, cafeMe.z + Math.sin(base + a) * len));
  if (turn === undefined) { cafeMe.target = null; cafeMe.pendingSeat = null; return; }
  dx = Math.cos(base + turn);
  dz = Math.sin(base + turn);
  cafeMe.x += dx * len;
  cafeMe.z += dz * len;
  let diff = Math.atan2(-dx, -dz) - cafeMe.yaw;
  diff = Math.atan2(Math.sin(diff), Math.cos(diff));
  cafeMe.yaw += diff * Math.min(1, d * 8);
}

export function NameTag({ state, name, color = "#064e3b" }) {
  const tag = useRef();
  useFrame(() => { if (tag.current) tag.current.position.set(state.x, state.sit ? 1.75 : 2.15, state.z); });
  return (
    <group ref={tag}>
      <Billboard><Text fontSize={0.17} color={color} outlineWidth={0.012} outlineColor="#ffffff" anchorY="bottom">{name}</Text></Billboard>
    </group>
  );
}

export function CafeAvatar({ gender, name }) {
  useEffect(() => {
    const kd = (e) => { if (!["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) keys.add(e.key.toLowerCase()); };
    const ku = (e) => keys.delete(e.key.toLowerCase());
    window.addEventListener("keydown", kd);
    window.addEventListener("keyup", ku);
    return () => { window.removeEventListener("keydown", kd); window.removeEventListener("keyup", ku); keys.clear(); };
  }, []);
  useFrame((_, dt) => step(Math.min(dt, 0.05)));
  return (
    <>
      <UserAvatar gender={gender} state={cafeMe} />
      <NameTag state={cafeMe} name={name || "Tu"} />
    </>
  );
}
